import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const processed = useRef(false); // prevent React StrictMode double-invoke
  const [status, setStatus] = useState('Processing login...');

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const handleCallback = async () => {
      const hash = window.location.hash || '';
      const fragment = hash.startsWith('#') ? hash.substring(1) : '';
      const params = new URLSearchParams(fragment);

      // 1) Direct token (legacy email-link / magic flow)
      const directToken = params.get('token') || (fragment && !fragment.includes('=') ? fragment : null);
      if (directToken) {
        // Set a temporary localStorage token so AuthContext can run its
        // one-shot migration (sends Authorization header → backend reissues
        // an httpOnly cookie → localStorage is purged).
        localStorage.setItem('longevity_token', directToken);
        if (refreshUser) await refreshUser().catch(() => {});
        navigate('/dashboard', { replace: true });
        return;
      }

      // 2) Emergent Google Auth — exchange session_id for our app JWT
      const sessionId = params.get('session_id');
      if (sessionId) {
        try {
          setStatus('Finishing Google sign-in...');
          // The exchange endpoint sets the httpOnly auth cookie via
          // Set-Cookie; withCredentials lets the browser store + replay it.
          await axios.post(
            `${API}/auth/google/exchange`,
            { session_id: sessionId },
            { headers: { 'Content-Type': 'application/json' }, withCredentials: true },
          );
          if (refreshUser) await refreshUser().catch(() => {});
          navigate('/dashboard', { replace: true });
        } catch (e) {
          console.warn('Google exchange failed:', e);
          setStatus('Google sign-in failed. Redirecting…');
          setTimeout(() => navigate('/auth', { replace: true }), 1500);
        }
        return;
      }

      // No recognizable params - send back to /auth
      navigate('/auth', { replace: true });
    };

    handleCallback();
  }, [navigate, refreshUser]);

  return (
    <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center text-gray-500" data-testid="auth-callback">
      {status}
    </div>
  );
};

export default AuthCallback;
